import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Box,
    Alert,
    InputAdornment,
    Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { communicationChannelsApi } from '../../api/communicationChannelsApi';
import { updateWorkerRequest } from '../../api/workersApi';
import {
    CommunicationChannel,
    CommunicationChannelType,
    CommunicationChannelTypeLabels,
    CreateCommunicationChannelDto,
    UpdateCommunicationChannelDto,
    validateChannelValue,
    getChannelValuePlaceholder,
    getChannelValueType
} from '../../types/communication-channel';

interface CommunicationChannelDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    workerId: string;
    channel?: CommunicationChannel | null;
}

const CommunicationChannelDialog: React.FC<CommunicationChannelDialogProps> = ({
    open,
    onClose,
    onSave,
    workerId,
    channel
}) => {
    const [formData, setFormData] = useState<CreateCommunicationChannelDto>({
        channelType: CommunicationChannelType.EMAIL,
        channelValue: '',
        label: '',
        isPrimary: false,
        isVerified: false
    });
    const [errors, setErrors] = useState<{ channelValue?: string }>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMsg, setToastMsg] = useState('');

    useEffect(() => {
        if (channel) {
            setFormData({
                channelType: channel.channelType,
                channelValue: channel.channelValue,
                label: channel.label || '',
                isPrimary: channel.isPrimary,
                isVerified: channel.isVerified,
                metadata: channel.metadata || undefined
            });
        } else {
            setFormData({
                channelType: CommunicationChannelType.EMAIL,
                channelValue: '',
                label: '',
                isPrimary: false,
                isVerified: false
            });
        }
        setErrors({});
        setSaveError(null);
    }, [channel, open]);

    const handleChange = (field: keyof CreateCommunicationChannelDto, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error when user types
        if (field === 'channelValue') {
            setErrors(prev => ({ ...prev, channelValue: undefined }));
        }
    };

    const handleSubmit = async () => {
        // Validate channel value
        const validation = validateChannelValue(formData.channelType, formData.channelValue);
        if (!validation.isValid) {
            setErrors({ channelValue: validation.error });
            return;
        }

        setSaving(true);
        setSaveError(null);

        try {
            if (channel) {
                // Update existing channel
                const updateData: UpdateCommunicationChannelDto = {};
                
                if (formData.channelType !== channel.channelType) {
                    updateData.channelType = formData.channelType;
                }
                if (formData.channelValue !== channel.channelValue) {
                    updateData.channelValue = formData.channelValue;
                }
                if (formData.label !== channel.label) {
                    updateData.label = formData.label || null;
                }
                if (formData.isPrimary !== channel.isPrimary) {
                    updateData.isPrimary = formData.isPrimary;
                }
                if (formData.isVerified !== channel.isVerified) {
                    updateData.isVerified = formData.isVerified;
                }

                await communicationChannelsApi.updateChannel(channel.id, updateData);
                // Also push to worker fields if applicable
                try {
                    if (updateData.channelValue) {
                        if (formData.channelType === CommunicationChannelType.EMAIL) {
                            await updateWorkerRequest({ workerId, emailAddress: updateData.channelValue });
                            setToastMsg('Worker email saved');
                            setToastOpen(true);
                        }
                        if ([CommunicationChannelType.PHONE, CommunicationChannelType.WHATSAPP, CommunicationChannelType.TELEGRAM].includes(formData.channelType)) {
                            await updateWorkerRequest({ workerId, phoneNumber: updateData.channelValue });
                            setToastMsg('Worker phone saved');
                            setToastOpen(true);
                        }
                    }
                } catch (e) { /* non-blocking */ }
            } else {
                // Create new channel
                await communicationChannelsApi.createChannel(workerId, formData);
                // Also push to worker fields if applicable
                try {
                    if (formData.channelType === CommunicationChannelType.EMAIL) {
                        await updateWorkerRequest({ workerId, emailAddress: formData.channelValue });
                        setToastMsg('Worker email saved');
                        setToastOpen(true);
                    }
                    if ([CommunicationChannelType.PHONE, CommunicationChannelType.WHATSAPP, CommunicationChannelType.TELEGRAM].includes(formData.channelType)) {
                        await updateWorkerRequest({ workerId, phoneNumber: formData.channelValue });
                        setToastMsg('Worker phone saved');
                        setToastOpen(true);
                    }
                } catch (e) { /* non-blocking */ }
            }

            // If a toast is shown, delay close slightly so the user can see it
            if (toastOpen) {
                setTimeout(() => {
                    onSave();
                }, 800);
            } else {
                onSave();
            }
        } catch (error: any) {
            console.error('Error saving channel:', error);
            setSaveError(error.response?.data?.message || 'Failed to save communication channel');
        } finally {
            setSaving(false);
        }
    };

    const getInputType = () => {
        const valueType = getChannelValueType(formData.channelType);
        switch (valueType) {
            case 'email':
                return 'email';
            case 'phone':
                return 'tel';
            case 'url':
                return 'url';
            default:
                return 'text';
        }
    };

    const getInputPrefix = () => {
        if (formData.channelType === CommunicationChannelType.INSTAGRAM) {
            return '@';
        }
        return undefined;
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {channel ? 'Edit Communication Channel' : 'Add Communication Channel'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {saveError && (
                        <Alert severity="error" onClose={() => setSaveError(null)}>
                            {saveError}
                        </Alert>
                    )}

                    <FormControl fullWidth>
                        <InputLabel>Channel Type</InputLabel>
                        <Select
                            value={formData.channelType}
                            onChange={(e) => handleChange('channelType', e.target.value)}
                            label="Channel Type"
                        >
                            {Object.entries(CommunicationChannelTypeLabels).map(([type, label]) => (
                                <MenuItem key={type} value={type}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Value"
                        value={formData.channelValue}
                        onChange={(e) => handleChange('channelValue', e.target.value)}
                        fullWidth
                        required
                        error={!!errors.channelValue}
                        helperText={errors.channelValue || getChannelValuePlaceholder(formData.channelType)}
                        type={getInputType()}
                        InputProps={{
                            startAdornment: getInputPrefix() ? (
                                <InputAdornment position="start">{getInputPrefix()}</InputAdornment>
                            ) : undefined
                        }}
                    />

                    <TextField
                        label="Label (optional)"
                        value={formData.label}
                        onChange={(e) => handleChange('label', e.target.value)}
                        fullWidth
                        placeholder="e.g., Work, Personal, Main"
                        helperText="Optional label to identify this channel"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.isPrimary || false}
                                onChange={(e) => handleChange('isPrimary', e.target.checked)}
                            />
                        }
                        label="Set as primary channel"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.isVerified || false}
                                onChange={(e) => handleChange('isVerified', e.target.checked)}
                            />
                        }
                        label="Mark as verified"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={saving || !formData.channelValue.trim()}
                >
                    {saving ? 'Saving...' : (channel ? 'Update' : 'Add')}
                </Button>
            </DialogActions>
        </Dialog>
        <Snackbar
            open={toastOpen}
            onClose={() => setToastOpen(false)}
            autoHideDuration={1500}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <MuiAlert onClose={() => setToastOpen(false)} severity="success" variant="filled" elevation={6}>
                {toastMsg}
            </MuiAlert>
        </Snackbar>
        </>
    );
};

export default CommunicationChannelDialog;
